/**
 * `@dxf-ai-editor/cad-core` — the 2×2 LINEAR part of a block-reference
 * transform, in the `R(θ)·S(a,b)·R(φ)` (SVD) shape, plus the ONE exact rule
 * for composing an enclosing reference onto an inner one.
 *
 * # Why this lives in cad-core (Codex PR #784 round 7 P2)
 *
 * Two consumers must agree bit-for-bit on the SAME composition:
 *
 *   - the RENDERER (`apps/web/.../scene3d/adapters/typed/blockFromEntity.ts`
 *     and `blockChildMultileader.ts`) accumulates it while flattening a block
 *     reference into world-space renderables;
 *   - the BOUNDS walk (`./blockDefinitionWalk.ts`) must reproduce the
 *     renderer's accumulated scale/rotation to bound what the renderer
 *     actually draws (the recursion placeholder — `./recursionPlaceholder.ts`).
 *
 * cad-core cannot import from `apps/web` (`.dependency-cruiser.cjs`
 * `cad-core-no-back-edges`), so the math lives HERE and the renderer imports
 * it — never the reverse, and never a second copy.
 *
 * # The shape
 *
 * A block reference's linear part is authored as a TRIPLE (per-axis scale +
 * one rotation), which has 3 degrees of freedom. A general 2×2 linear map has
 * 4, so a triple can NOT express `S(a,b)·R(φ)` when `|a| ≠ |b|` — that product
 * is a genuine SHEAR. `R(θ)·S(a,b)·R(φ)` IS the singular-value decomposition
 * shape, so it can express ANY 2D linear map, and every 2×2 matrix has such a
 * decomposition in closed form ({@link decomposeBlockLinear}) — no iteration,
 * no approximation.
 *
 * Signs: `a` is non-negative and `b` carries the determinant's sign, so a
 * MIRRORED composition stays representable as a negative scale component
 * (which is exactly what the rest of the stack already means by a negative
 * scale — `isMirroredTransform`, `|scaleX|` magnitude conventions).
 */

/** A 2×2 linear map, row-major: `[[m00, m01], [m10, m11]]`. */
export interface Mat2 {
  readonly m00: number;
  readonly m01: number;
  readonly m10: number;
  readonly m11: number;
}

/**
 * The linear part of a block reference in the SVD shape
 *
 *     L = R(rotationDeg) · S(scaleX, scaleY) · R(preRotationDeg)
 *
 * — the OUTER rotation, the signed per-axis scale, and the INNER rotation
 * applied to the block-local vector FIRST. `preRotationDeg: 0` degrades to
 * the pre-existing 3-DOF triple, which is what every plain INSERT is.
 */
export interface BlockLinearRsr {
  readonly rotationDeg: number;
  readonly scaleX: number;
  readonly scaleY: number;
  readonly preRotationDeg: number;
}

/** An authored block reference's own triple (a DXF INSERT: groups 41/42, 50). */
export interface BlockLinearTriple {
  readonly rotationDeg: number;
  readonly scaleX: number;
  readonly scaleY: number;
}

/** The identity linear part — the WORLD frame, before any reference. */
export const IDENTITY_BLOCK_LINEAR: BlockLinearRsr = {
  rotationDeg: 0,
  scaleX: 1,
  scaleY: 1,
  preRotationDeg: 0,
};

/**
 * Degrees → radians with the flatten's operation order `(deg · π) / 180`
 * (`blockTransform.ts::degToRad` — the golden-parity harness compares the
 * flatten outputs bit-exactly, and `deg · (π/180)` can differ by an ulp).
 */
function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function radToDeg(rad: number): number {
  return (rad * 180) / Math.PI;
}

/**
 * The scale-component guard the typed flatten applies to EVERY reference
 * scale it accumulates (`blockFromEntity.ts`'s `guardScale`, itself the
 * legacy `flattenBlockInsertEntity:1080-1084` rule): a degenerate
 * (non-finite / ~zero) component falls back to 1, because
 * `blockInsertEntitySchema` permits 0 — which would collapse the subtree to a
 * point and blow the `|sx|/|sy|` width-factor ratio to Infinity.
 *
 * ONE truth: the renderer imports this instead of re-spelling it, so the
 * bounds walk's accumulated scale cannot drift from the flatten's.
 */
export function guardBlockScale(value: number): number {
  return Number.isFinite(value) && Math.abs(value) > 1e-9 ? value : 1;
}

/**
 * Every value finite? ONE guard shared by the two failure-tolerant returns
 * below, so a single non-finite component is decided in one place (and a test
 * can isolate each component instead of facing a four-way conjunction).
 */
function allFinite(values: readonly number[]): boolean {
  for (const v of values) {
    if (!Number.isFinite(v)) return false;
  }
  return true;
}

/** The 2×2 matrix of `R(θ)·S(a,b)·R(φ)`. */
export function blockLinearMatrix(t: BlockLinearRsr): Mat2 {
  const th = degToRad(t.rotationDeg);
  const ph = degToRad(t.preRotationDeg);
  const ct = Math.cos(th);
  const st = Math.sin(th);
  const cp = Math.cos(ph);
  const sp = Math.sin(ph);
  const a = t.scaleX;
  const b = t.scaleY;
  // R(θ)·S(a,b)·R(φ), expanded once (see the module doc's algebra).
  return {
    m00: a * ct * cp - b * st * sp,
    m01: -a * ct * sp - b * st * cp,
    m10: a * st * cp + b * ct * sp,
    m11: -a * st * sp + b * ct * cp,
  };
}

/**
 * Closed-form 2×2 SVD: the unique-up-to-sign `R(θ)·S(a,b)·R(φ)` whose matrix
 * is `m`, with `a ≥ 0` and `b` carrying `sign(det m)`.
 *
 * # Derivation
 *
 * Writing `m` out from `R(θ)·S(a,b)·R(φ)` and forming the four combinations
 *
 *     E = (m00 + m11)/2 = ((a + b)/2)·cos(θ + φ)
 *     H = (m10 − m01)/2 = ((a + b)/2)·sin(θ + φ)
 *     F = (m00 − m11)/2 = ((a − b)/2)·cos(θ − φ)
 *     G = (m10 + m01)/2 = ((a − b)/2)·sin(θ − φ)
 *
 * gives `|a + b|/2 = hypot(E, H)` and `|a − b|/2 = hypot(F, G)`, while
 * `atan2` recovers the angle SUM and DIFFERENCE directly. Choosing the
 * non-negative root for both magnitudes fixes `a = hypot(E,H) + hypot(F,G)`
 * (≥ 0) and `b = hypot(E,H) − hypot(F,G)` (negative exactly when `det < 0`,
 * i.e. a mirror), and `θ = (sum + diff)/2`, `φ = (sum − diff)/2`.
 *
 * Every branch is total: `atan2(0, 0)` is 0 (a uniform scale leaves φ free —
 * 0 is picked), so the only failure mode is a non-finite input, which returns
 * `null` for the caller's own fallback.
 */
export function decomposeBlockLinear(m: Mat2): BlockLinearRsr | null {
  const e = (m.m00 + m.m11) / 2;
  const h = (m.m10 - m.m01) / 2;
  const f = (m.m00 - m.m11) / 2;
  const g = (m.m10 + m.m01) / 2;
  const q = Math.hypot(e, h);
  const r = Math.hypot(f, g);
  const sum = Math.atan2(h, e);
  const diff = Math.atan2(g, f);
  const out: BlockLinearRsr = {
    rotationDeg: radToDeg((sum + diff) / 2),
    scaleX: q + r,
    scaleY: q - r,
    preRotationDeg: radToDeg((sum - diff) / 2),
  };
  return allFinite([out.rotationDeg, out.scaleX, out.scaleY, out.preRotationDeg])
    ? out
    : null;
}

/**
 * The EXACT composition `outer ∘ R(inner.rotationDeg)·S(inner.scale…)` — the
 * enclosing reference's linear part applied to an inner reference's own
 * triple, re-expressed in the same `R·S·R` shape.
 *
 * Two fast paths, both EXACT (not approximations — they are the closed forms
 * of the same product, and they keep the pre-existing arithmetic
 * byte-identical for the cases that already worked):
 *
 *   - a UNIFORM inner scale COMMUTES with the inner rotation
 *     (`S(a,b)·R(φ)·R(r)·S(c,c) = S(a·c, b·c)·R(φ + r)`), so the product is
 *     the per-axis scale PRODUCT with the rotations summed into `φ`;
 *   - otherwise the full 2×2 product is decomposed
 *     ({@link decomposeBlockLinear}). Codex PR #784 round 7 P2: with an outer
 *     `S(3,2)`, an inner rotation of 45° and a nested scale `(2,1)`, folding
 *     the nested scale into the outer one emits `(1,0)` at `(4.24, 1.41)`
 *     instead of the correct `(4.24, 2.83)` — the nested scale must stay on
 *     the RIGHT of the rotation, which only the decomposition can express.
 *
 * Returns the uniform-style fold when the decomposition is not finite (a
 * degenerate matrix), so a caller never propagates NaN geometry.
 */
export function composeBlockLinearExact(
  outer: BlockLinearRsr,
  inner: BlockLinearTriple,
): BlockLinearRsr {
  // `R(θ)·S(a,b)·R(φ + r)` — the inner ROTATION always joins the INNER slot,
  // because it is already the innermost rotation of the product.
  const rotated: BlockLinearRsr = {
    rotationDeg: outer.rotationDeg,
    scaleX: outer.scaleX,
    scaleY: outer.scaleY,
    preRotationDeg: outer.preRotationDeg + inner.rotationDeg,
  };
  const commuted: BlockLinearRsr = {
    ...rotated,
    scaleX: outer.scaleX * inner.scaleX,
    scaleY: outer.scaleY * inner.scaleY,
  };
  if (inner.scaleX === inner.scaleY) return commuted;
  // …· S(cx, cy) on the RIGHT: right-multiplying by a diagonal scales the
  // COLUMNS, which is precisely the step the per-axis fold above gets wrong
  // for a non-uniform inner scale.
  const m = blockLinearMatrix(rotated);
  const product: Mat2 = {
    m00: m.m00 * inner.scaleX,
    m01: m.m01 * inner.scaleY,
    m10: m.m10 * inner.scaleX,
    m11: m.m11 * inner.scaleY,
  };
  return decomposeBlockLinear(product) ?? commuted;
}

/**
 * The NESTED-INSERT accumulation rule of the typed flatten
 * (`blockFromEntity.ts`'s nested `Block` arm) — the ONE truth the bounds walk
 * replays to reproduce the renderer's accumulated transform.
 *
 * ALWAYS the exact sequential product
 *
 *     L_world = L_outer · R(inner.rotation) · S(inner.scaleX, inner.scaleY)
 *
 * which is AutoCAD's own nested-reference semantics: a nested INSERT is
 * evaluated in the enclosing reference's frame, so its own rotation and scale
 * apply INSIDE that frame (`M_world = M_outer · M_inner` — Autodesk DXF
 * reference INSERT/BLOCK, matched by `ezdxf`'s `Insert.matrix44()` chain and
 * by this repo's own exact affine chains: `cad-core`'s bounds walk
 * (`blockDefinitionWalk.ts` → `transformBoundsByInsert`) and its select
 * geometry (`blockSelectGeometry.ts`) both compose it per level).
 *
 * # Codex PR #784 round 8 P2 — why the legacy TRIPLE fold had to go
 *
 * Round 7 kept a "legacy, golden-parity pinned" fold for the no-inner-rotation
 * case: rotations SUMMED, per-axis scales MULTIPLIED, i.e.
 * `R(θ + r)·S(a·cx, b·cy)`. That equals the product above ONLY when the outer
 * scale is uniform (`a = b`) or the inner rotation is absent — with a
 * NON-UNIFORM outer scale around a ROTATED nested INSERT it emits
 * `R(θ + r)·S(a·cx, b·cy)` where the truth is `R(θ)·S(a,b)·R(r)·S(cx,cy)`, and
 * the difference is a real SHEAR, not a convention.
 *
 * Concretely (the finding's fixture — `blockRecursionPlaceholderBounds.test.ts`
 * "S(3,1) · R(45°)"): a definition holding the line `(0,0)–(10,0)`, referenced
 * by a nested INSERT rotated 45°, under a top INSERT scaled `(3, 1)`. The fold
 * drew the far end at `(21.21, 21.21)`; the exact chain — and therefore the
 * AABB `resolveBlockInsertBounds` stores in the RBush — puts it at
 * `(21.21, 7.07)`. On the cache-direct path the stored bounds then EXCLUDED
 * visible strokes, so viewport culling and broad-phase picking lost them.
 *
 * # The two fast paths
 *
 * Both are the CLOSED FORM of the same product, not approximations, so they
 * are kept only where they are provably byte-identical to it:
 *
 *   - `inner.rotationDeg === 0` ⇒ `R(0) = I` and two diagonals multiply
 *     component-wise: `R(θ)·S(a,b)·S(cx,cy) = R(θ)·S(a·cx, b·cy)`;
 *   - `outer.scaleX === outer.scaleY` ⇒ a uniform scale is the SCALAR `a·I`,
 *     which commutes with the inner rotation:
 *     `R(θ)·S(a,a)·R(r)·S(cx,cy) = R(θ + r)·S(a·cx, a·cy)`.
 *
 * Both require `outer.preRotationDeg === 0` (an inner rotation already in
 * force — the MLEADER block-content reference — cannot be folded outward at
 * all). Everything else goes through {@link composeBlockLinearExact}, which
 * decomposes the true 2×2 product back into the `R·S·R` shape.
 */
export function composeNestedBlockLinear(
  outer: BlockLinearRsr,
  inner: BlockLinearTriple,
): BlockLinearRsr {
  if (
    !outer.preRotationDeg &&
    (!inner.rotationDeg || outer.scaleX === outer.scaleY)
  ) {
    return {
      rotationDeg: outer.rotationDeg + inner.rotationDeg,
      scaleX: outer.scaleX * inner.scaleX,
      scaleY: outer.scaleY * inner.scaleY,
      preRotationDeg: 0,
    };
  }
  return composeBlockLinearExact(outer, inner);
}

/**
 * The inverse linear map (world → the reference's local frame), or `null`
 * when the map is singular / non-finite (a degenerate scale). Plain 2×2
 * inverse of {@link blockLinearMatrix} — exact for every invertible input.
 */
export function invertBlockLinear(t: BlockLinearRsr): Mat2 | null {
  const m = blockLinearMatrix(t);
  const det = m.m00 * m.m11 - m.m01 * m.m10;
  // Equivalent-mutant note (targeted mutation, PR #784 round 7): dropping the
  // `det === 0` operand alone SURVIVES and is equivalent — a zero determinant
  // divides every entry to ±Infinity or NaN, which the finite check below
  // rejects with the same `null`. It is kept as the guard's real contract (and
  // because it is NOT redundant for the `!Number.isFinite(det)` half: an
  // OVERFLOWED determinant divides the entries down to finite zeros, which
  // would otherwise pass — pinned by `blockLinearTransform.test.ts`'s
  // determinant-overflow case). Deliberately NOT suppressed: a per-mutator
  // `Stryker disable` would also hide the killable twins on this line.
  if (!Number.isFinite(det) || det === 0) return null;
  const inv: Mat2 = {
    m00: m.m11 / det,
    m01: -m.m01 / det,
    m10: -m.m10 / det,
    m11: m.m00 / det,
  };
  return allFinite([inv.m00, inv.m01, inv.m10, inv.m11]) ? inv : null;
}

/** Apply a 2×2 map to a vector. */
export function applyMat2(m: Mat2, x: number, y: number): { x: number; y: number } {
  return { x: m.m00 * x + m.m01 * y, y: m.m10 * x + m.m11 * y };
}
